/**
 * Destinations a trip manager can pin a WhatsApp reminder to.
 *
 * A curated list, not the full IANA database. There are ~600 IANA zones and a
 * manager knows none of them by name -- the field this replaced was a free-text
 * box expecting "Europe/Rome", where a plausible typo like "Europe/Roma" was
 * simply rejected. So each row is country and city in the manager's own
 * language, and the IANA name is only ever the stored value.
 *
 * Deliberately short. Forty entries cover where these travellers actually go;
 * guessing at a hundred now just makes a list nobody reads. Add what turns out
 * to be missing.
 *
 * Offsets are NOT stored here. They move twice a year with daylight saving, so
 * a hardcoded "+2" would be wrong for half of every year. They are computed at
 * render time from the browser's own timezone database instead -- see
 * offsetLabel below.
 */

export const TIMEZONE_GROUPS = [
  {
    key: 'common',
    zones: [
      { value: 'Asia/Jerusalem', he: 'ישראל · ירושלים', en: 'Israel · Jerusalem' },
      { value: 'Europe/Rome', he: 'איטליה · רומא', en: 'Italy · Rome' },
      { value: 'Europe/Athens', he: 'יוון · אתונה', en: 'Greece · Athens' },
      { value: 'Europe/Madrid', he: 'ספרד · מדריד', en: 'Spain · Madrid' },
      { value: 'Europe/Paris', he: 'צרפת · פריז', en: 'France · Paris' },
      { value: 'Europe/London', he: 'בריטניה · לונדון', en: 'United Kingdom · London' },
    ],
  },
  {
    key: 'europe',
    zones: [
      { value: 'Europe/Amsterdam', he: 'הולנד · אמסטרדם', en: 'Netherlands · Amsterdam' },
      { value: 'Europe/Berlin', he: 'גרמניה · ברלין', en: 'Germany · Berlin' },
      { value: 'Europe/Vienna', he: 'אוסטריה · וינה', en: 'Austria · Vienna' },
      { value: 'Europe/Prague', he: 'צ׳כיה · פראג', en: 'Czechia · Prague' },
      { value: 'Europe/Budapest', he: 'הונגריה · בודפשט', en: 'Hungary · Budapest' },
      { value: 'Europe/Warsaw', he: 'פולין · ורשה', en: 'Poland · Warsaw' },
      { value: 'Europe/Lisbon', he: 'פורטוגל · ליסבון', en: 'Portugal · Lisbon' },
      { value: 'Europe/Zurich', he: 'שווייץ · ציריך', en: 'Switzerland · Zurich' },
      { value: 'Europe/Copenhagen', he: 'דנמרק · קופנהגן', en: 'Denmark · Copenhagen' },
      { value: 'Europe/Oslo', he: 'נורווגיה · אוסלו', en: 'Norway · Oslo' },
      { value: 'Europe/Stockholm', he: 'שוודיה · שטוקהולם', en: 'Sweden · Stockholm' },
      { value: 'Europe/Dublin', he: 'אירלנד · דבלין', en: 'Ireland · Dublin' },
      { value: 'Europe/Istanbul', he: 'טורקיה · איסטנבול', en: 'Turkey · Istanbul' },
      { value: 'Asia/Nicosia', he: 'קפריסין · ניקוסיה', en: 'Cyprus · Nicosia' },
      { value: 'Asia/Tbilisi', he: 'גאורגיה · טביליסי', en: 'Georgia · Tbilisi' },
    ],
  },
  {
    key: 'asia',
    zones: [
      { value: 'Asia/Bangkok', he: 'תאילנד · בנגקוק', en: 'Thailand · Bangkok' },
      { value: 'Asia/Tokyo', he: 'יפן · טוקיו', en: 'Japan · Tokyo' },
      { value: 'Asia/Ho_Chi_Minh', he: 'וייטנאם · הו צ׳י מין', en: 'Vietnam · Ho Chi Minh City' },
      { value: 'Asia/Kolkata', he: 'הודו · דלהי', en: 'India · Delhi' },
      { value: 'Asia/Kathmandu', he: 'נפאל · קטמנדו', en: 'Nepal · Kathmandu' },
      { value: 'Asia/Colombo', he: 'סרי לנקה · קולומבו', en: 'Sri Lanka · Colombo' },
      { value: 'Asia/Jakarta', he: 'אינדונזיה · באלי', en: 'Indonesia · Bali' },
      { value: 'Asia/Manila', he: 'פיליפינים · מנילה', en: 'Philippines · Manila' },
      { value: 'Asia/Dubai', he: 'איחוד האמירויות · דובאי', en: 'United Arab Emirates · Dubai' },
      { value: 'Asia/Amman', he: 'ירדן · עמאן', en: 'Jordan · Amman' },
    ],
  },
  {
    key: 'americas',
    zones: [
      { value: 'America/New_York', he: 'ארה״ב · ניו יורק', en: 'United States · New York' },
      { value: 'America/Chicago', he: 'ארה״ב · שיקגו', en: 'United States · Chicago' },
      { value: 'America/Denver', he: 'ארה״ב · דנוור', en: 'United States · Denver' },
      { value: 'America/Los_Angeles', he: 'ארה״ב · לוס אנג׳לס', en: 'United States · Los Angeles' },
      { value: 'America/Toronto', he: 'קנדה · טורונטו', en: 'Canada · Toronto' },
      { value: 'America/Mexico_City', he: 'מקסיקו · מקסיקו סיטי', en: 'Mexico · Mexico City' },
      { value: 'America/Sao_Paulo', he: 'ברזיל · סאו פאולו', en: 'Brazil · Sao Paulo' },
      { value: 'America/Argentina/Buenos_Aires', he: 'ארגנטינה · בואנוס איירס', en: 'Argentina · Buenos Aires' },
      { value: 'America/Lima', he: 'פרו · לימה', en: 'Peru · Lima' },
      { value: 'America/Bogota', he: 'קולומביה · בוגוטה', en: 'Colombia · Bogota' },
    ],
  },
  {
    key: 'africaOceania',
    zones: [
      { value: 'Africa/Casablanca', he: 'מרוקו · קזבלנקה', en: 'Morocco · Casablanca' },
      { value: 'Africa/Cairo', he: 'מצרים · קהיר', en: 'Egypt · Cairo' },
      { value: 'Africa/Nairobi', he: 'קניה · ניירובי', en: 'Kenya · Nairobi' },
      { value: 'Africa/Johannesburg', he: 'דרום אפריקה · יוהנסבורג', en: 'South Africa · Johannesburg' },
      { value: 'Australia/Sydney', he: 'אוסטרליה · סידני', en: 'Australia · Sydney' },
      { value: 'Pacific/Auckland', he: 'ניו זילנד · אוקלנד', en: 'New Zealand · Auckland' },
    ],
  },
];

/**
 * Current UTC offset for an IANA zone, e.g. "UTC+02:00".
 *
 * Read from the browser's own timezone database at render time rather than
 * stored, so it is automatically right on both sides of a daylight-saving
 * change. Returns '' rather than throwing on an unknown zone or an engine
 * without longOffset support -- a missing offset is a cosmetic loss, and it
 * must never stop the dropdown rendering.
 */
export function offsetLabel( zone, at = new Date() ) {
  try {
    const parts = new Intl.DateTimeFormat( 'en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    } ).formatToParts( at );
    const name = parts.find( ( part ) => part.type === 'timeZoneName' );
    return name ? name.value.replace( 'GMT', 'UTC' ) : '';
  } catch ( error ) {
    return '';
  }
}
